import type { CustomLoginFormMessages } from "./CustomLoginForm";

export type PasswordValidation = {
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialCharacter: boolean;
  hasSufficientLength: boolean;
  matchesConfirm: boolean;
};

export const INITIAL_PASSWORD_VALIDATION: PasswordValidation = {
  hasUppercase: false,
  hasLowercase: false,
  hasNumber: false,
  hasSpecialCharacter: false,
  hasSufficientLength: false,
  matchesConfirm: true,
};

const SSO_REDIRECT_KEY = "sso_post_login_redirect";

export function isSafeRedirect(redirect: unknown): redirect is string {
  return (
    typeof redirect === "string" &&
    redirect.startsWith("/") &&
    !redirect.startsWith("//") &&
    !redirect.startsWith("/\\")
  );
}

export function validateEmail(email: string) {
  return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})$/.test(
    email,
  );
}

export function validateNewPassword(
  password: string,
  confirmPassword: string,
): PasswordValidation {
  return {
    hasUppercase: password.toLowerCase() !== password,
    hasLowercase: password.toUpperCase() !== password,
    hasNumber: /[0-9]/.test(password),
    hasSpecialCharacter: /[^a-zA-Z0-9]/.test(password),
    hasSufficientLength: password.length >= 10 && password.length <= 32,
    matchesConfirm: password === confirmPassword,
  };
}

export function consumeSsoRedirect() {
  const redirect = sessionStorage.getItem(SSO_REDIRECT_KEY);
  sessionStorage.removeItem(SSO_REDIRECT_KEY);
  return isSafeRedirect(redirect) ? redirect : null;
}

export function saveSsoRedirect(redirect: string) {
  sessionStorage.setItem(SSO_REDIRECT_KEY, redirect);
}

export function getErrorMessage(
  error: unknown,
  messages: CustomLoginFormMessages,
): string {
  const cognitoError = error as {
    name?: string;
    code?: string;
    message?: string;
    __type?: string;
  };
  const name =
    cognitoError.name ??
    cognitoError.code ??
    cognitoError.__type?.split("#").pop() ??
    "";
  const message = String(cognitoError.message ?? "");

  if (
    (name === "NotAuthorizedException" || name === "NotAuthorized") &&
    /incorrect username or password/i.test(message)
  ) {
    return messages.errors.incorrectEmailOrPassword;
  }

  if (name === "UserNotFoundException") {
    return messages.errors.incorrectEmailOrPassword;
  }

  if (name === "UserNotConfirmedException") {
    return messages.errors.accountNotConfirmed;
  }

  if (name === "PasswordResetRequiredException") {
    return messages.errors.passwordResetRequired;
  }

  if (
    name === "LimitExceededException" ||
    name === "TooManyRequestsException"
  ) {
    return messages.errors.tooManyAttempts;
  }

  if (/Failed to fetch|NetworkError/i.test(message)) {
    return messages.errors.network;
  }

  return messages.errors.generic;
}

export function isIncorrectCredentialsError(error: unknown) {
  const cognitoError = error as {
    name?: string;
    code?: string;
    message?: string;
    __type?: string;
  };
  const name =
    cognitoError.name ??
    cognitoError.code ??
    cognitoError.__type?.split("#").pop() ??
    "";
  const message = String(cognitoError.message ?? "");

  return (
    name === "UserNotFoundException" ||
    ((name === "NotAuthorizedException" || name === "NotAuthorized") &&
      /incorrect username or password/i.test(message))
  );
}

export function getNewPasswordErrorMessage(
  error: unknown,
  messages: CustomLoginFormMessages,
): string {
  const cognitoError = error as {
    name?: string;
    code?: string;
    message?: string;
  };
  const name = cognitoError.name ?? cognitoError.code ?? "";
  const message = String(cognitoError.message ?? "");

  if (name === "PasswordHistoryPolicyViolationException") {
    return messages.errors.passwordHistoryViolation;
  }

  if (name === "InvalidPasswordException") {
    return messages.errors.invalidPasswordPolicy;
  }

  if (
    name === "LimitExceededException" ||
    name === "TooManyRequestsException"
  ) {
    return messages.errors.tooManyAttempts;
  }

  if (/Failed to fetch|NetworkError/i.test(message)) {
    return messages.errors.network;
  }

  return messages.errors.updatePasswordGeneric;
}

export function isNewPasswordFieldError(error: unknown) {
  const cognitoError = error as {
    name?: string;
    code?: string;
    __type?: string;
  };
  const name =
    cognitoError.name ??
    cognitoError.code ??
    cognitoError.__type?.split("#").pop() ??
    "";

  return (
    name === "PasswordHistoryPolicyViolationException" ||
    name === "InvalidPasswordException"
  );
}
